import { useCallback, useContext } from "react";
import { TaskType } from "@common/types/task";
import { FiltersTypes } from "@common/types/filters/general";
import { FinancialDocumentViewSchema } from "@common/types/views/financialDocumentViews";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useOverlays } from "contexts/OverlayContext";
import {
    buildBulkFinancialDocumentsTaskView,
    defaultFinancialDocumentsView,
} from "@components/tasks/TaskBody/FinancialDocuments/financialDocumentsTaskView";
import { FinancialDocumentsBulkTaskViewContext } from "./FinancialDocumentsBulkTaskView.context";

interface OpenFinancialDocumentsTaskParams {
    financialDocumentIds?: number[];
    filters?: FiltersTypes;
    entityIds?: number[];
}

export function useOpenFinancialDocumentsTask() {
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const taskView = useContext(FinancialDocumentsBulkTaskViewContext);
    const [pageView] = useWorkspaceUserCache(
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENTS_PAGE_VIEW_CACHE_KEY,
        FinancialDocumentViewSchema,
    );

    return useCallback(
        ({
            financialDocumentIds,
            filters,
            entityIds,
        }: OpenFinancialDocumentsTaskParams) => {
            // Opens a create form the server can only refuse on save (PRO-11345).
            if (refuseIfViewOnly()) {
                return;
            }
            const view =
                taskView ?? pageView ?? defaultFinancialDocumentsView();
            openOverlay("createTask", {
                type: TaskType.FINANCIAL_DOCUMENTS,
                viewConfig: buildBulkFinancialDocumentsTaskView({
                    view,
                    financialDocumentIds,
                    filters,
                    entityIds: filters ? entityIds : undefined,
                }),
                initialValues: { entityIds },
            });
        },
        [taskView, pageView, openOverlay, refuseIfViewOnly],
    );
}
